"use client";

import { type FC, type Ref, useMemo } from "react";
import { Flex, BasicCard, Text } from "@huub-web/ui";

import { TemplatesParser } from "../../components";
import type { TemplateCollection, TemplateIcon } from "../../types";
import { usePageContent } from "../../hooks/usePageContent";

export interface CardTemplateProps {
  /**
   * Markdown enabled
   */
  title: string;
  /**
   * Markdown enabled
   */
  body?: string;
  /**
   * Templates to render
   */
  templates: TemplateCollection;
  /**
   * Icon to display
   */
  icon?: TemplateIcon;
  /**
   * Header direction
   */
  headerDirection?: "horizontal" | "vertical";
  /**
   * Variant of the card
   */
  variant?: "primary" | "secondary";
  /**
   * Render the card elevated
   */
  elevated?: boolean;
  /**
   * Subtitle of the card
   */
  subtitle?: string;
}

export const CardTemplate: FC<
  CardTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  title,
  subtitle,
  body,
  templates,
  variant: variantProp = "primary",
  headerDirection,
  icon,
  elevated = false,
  ref,
}) => {
  const contentItem = useMemo(
    () =>
      title
        ? {
            type: "card" as const,
            label: title,
            ...(body ? { description: body } : {}),
          }
        : null,
    [title, body],
  );
  usePageContent("card", contentItem);

  return (
    <BasicCard
      ref={ref}
      gap="xs"
      variant={variantProp === "primary" ? "primary" : "tertiary"}
      headerDirection={headerDirection}
      icon={icon}
      title={title}
      subtitle={subtitle}
      border={variantProp === "primary" ? "default" : undefined}
      elevated={elevated}
      indicatorVariant={variantProp === "primary" ? "infoSurface" : undefined}
    >
      {body && (
        <Text variant="bodyS" color="currentColor50" markdown>
          {body}
        </Text>
      )}
      {templates && (
        <Flex direction="column" gap="s">
          {(title || body) && <div />}
          <TemplatesParser level={1} templates={templates} />
        </Flex>
      )}
    </BasicCard>
  );
};
